import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Consultation, FormQuestionResponse, QuestionOptionType, AnswerValue } from "@/types/consultation";
import { Control, ControllerRenderProps } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";

interface DynamicQuestionRendererProps {
  question: FormQuestionResponse;
  control: Control<Consultation>;
  index: number;
}

const DynamicQuestionRenderer = ({
  question,
  control,
  index,
}: DynamicQuestionRendererProps) => {
  const {
    id,
    question_text,
    question_type,
    options,
    is_required,
  } = question;
  
  const name = `answers.${id}` as `answers.${string}`;

  return (
    <FormField
      control={control}
      name={name}
      rules={{ required: (is_required && question_type !== "toggle") ? "This field is required" : false }}
      render={({ field }) => {
        if (question_type === "toggle") {
          return (
            <FormItem className="w-full mb-4">
              <div className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base font-normal text-gray-700">
                    {index}. {question_text}{" "}
                    {is_required && <span className="text-red-500">*</span>}
                  </FormLabel>
                </div>
                <FormControl>
                  {renderInput(question_type, field, options)}
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          );
        }

        return (
          <FormItem className="w-full mb-4">
            <div className="space-y-0.5 mb-2">
              <FormLabel className="text-base font-normal text-gray-700">
                {index}. {question_text}
                {question_type === "textarea" &&
                  options &&
                  options.length > 0 && (
                    <>
                      <br />
                      <span className="text-gray-500 text-sm ml-1">
                        ({options.map((opt: QuestionOptionType) => (typeof opt === 'string' ? opt : opt.label)).join(", ")})
                      </span>
                    </>
                  )}{" "}
                {is_required && <span className="text-red-500">*</span>}
              </FormLabel>
            </div>

            <FormControl>{renderInput(question_type, field, options)}</FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

const renderInput = (
  type: string,
  field: ControllerRenderProps<Consultation, `answers.${string}`>,
  options: QuestionOptionType[] | null | undefined
) => {
  switch (type) {
    case "text":
    case "email":
    case "number":
      return (
        <Input
          placeholder="Your Answer Here"
          {...field}
          value={(field.value as string | number) || ""}
          className="bg-white relative z-10"
        />
      );

    case "textarea":
      return (
        <Textarea
          placeholder="Your Answer Here"
          {...field}
          value={(field.value as string) || ""}
          className="bg-white min-h-[100px] relative z-10"
        />
      );

    case "toggle":
      return (
        <Switch
          checked={
            field.value === true ||
            field.value === "true" ||
            field.value === 1
          }
          onCheckedChange={(checked) => field.onChange(checked)}
        />
      );

    case "checkbox":
      if (!options || options.length === 0)
        return (
          <Input {...field} value={(field.value as string | number) || ""} />
        );

      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
          {options.map((opt: QuestionOptionType, idx: number) => {
            const label = typeof opt === "string" ? opt : opt.label;
            const value = typeof opt === "string" ? opt : opt.value;

            const isChecked = Array.isArray(field.value) && (field.value as AnswerValue[]).includes(value);

            return (
              <div key={idx} className="flex items-center space-x-2">
                <Checkbox
                  id={`checkbox-${idx}-${label}`}
                  checked={isChecked}
                  onCheckedChange={(checked) => {
                    const current = Array.isArray(field.value) ? (field.value as AnswerValue[]) : [];
                    if (checked) {
                      field.onChange([...current, value]);
                    } else {
                      field.onChange(current.filter((v: AnswerValue) => v !== value));
                    }
                  }}
                />
                <label
                  htmlFor={`checkbox-${idx}-${label}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  {label}
                </label>
              </div>
            );
          })}
        </div>
      );

    case "radio":
      if (!options || options.length === 0)
        return (
          <Input {...field} value={(field.value as string | number) || ""} />
        );

      return (
        <RadioGroup
          onValueChange={field.onChange}
          defaultValue={field.value as string}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-2"
        >
          {options.map((opt: QuestionOptionType, idx: number) => {
            const label = typeof opt === "string" ? opt : opt.label;
            const value = typeof opt === "string" ? opt : opt.value;
            return (
              <div key={idx} className="flex items-center space-x-3 space-y-0">
                <RadioGroupItem value={String(value)} id={`r-${idx}-${label}`} />
                <label
                  htmlFor={`r-${idx}-${label}`}
                  className="font-normal cursor-pointer"
                >
                   {label}
                </label>
              </div>
            );
          })}
        </RadioGroup>
      );

    case "select": // Handling select as buttons based on image if options are provided
      if (!options || options.length === 0)
        return (
          <Input
            {...field}
            value={(field.value as string | number) || ""}
          />
        );



      return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-2">
          {options.map((opt: QuestionOptionType, idx: number) => {
            // Option can be string or object {label, value}
            const label = typeof opt === "string" ? opt : opt.label;
            const value = typeof opt === "string" ? opt : opt.value;

            const isSelected = field.value === value;

            return (
              <div
                key={idx}
                onClick={() => {
                  field.onChange(value);
                }}
                className={cn(
                  "cursor-pointer text-center py-3 px-4 rounded-md border text-sm transition-all",
                  isSelected
                    ? "bg-[#6be27f] text-black border-[#6be27f] font-medium" // Matching the green in image
                    : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
                )}
              >
                {label}
              </div>
            );
          })}
        </div>
      );

    default:
      return (
        <Input
          {...field}
          value={(field.value as string | number) || ""}
           className="relative z-10"
        />
      );
  }
};

export default DynamicQuestionRenderer;
